import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { X, Search, Loader2, Building2, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { AddProspectAccountInline } from './AddProspectAccountInline';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ORANGE = '#ee7203';

/**
 * "NO CRM — ADD NOW": ties a parsed M4 quote's customer to the right CRM account.
 *
 * The user either picks the account from a CRM search (the match logic missed it) or creates a brand-new CRM customer. Either
 * way the customer NAME on the quote is remembered against that CRM account, so every future quote for that name is matched
 * and auto-added exactly like any other customer. If the account isn't in the Phocas Customer Database yet (so there is no
 * market sector to take the auto-add threshold from) the user is asked for the market sector once.
 *
 *   item      the Recent Quotes automation item (needs item.id and item.email_data)
 *   onLinked  called with { id, name } of the CRM account once the link has been saved
 */
export default function LinkCrmAccountModal({ isOpen, onClose, item, onLinked }) {
  const email = item?.email_data || {};
  const customerName = (email.invoice_customer_name || email.customer_name || '').trim();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pending, setPending] = useState(null); // the account waiting for a market sector
  const [sectorOptions, setSectorOptions] = useState([]);
  const [sector, setSector] = useState('');
  const seq = useRef(0);

  useEffect(() => {
    if (!isOpen) return;
    setQuery(customerName);
    setResults([]);
    setShowNew(false);
    setPending(null);
    setSectorOptions([]);
    setSector('');
  }, [isOpen, customerName]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const q = query.trim();
    if (q.length < 2) { setResults([]); return undefined; }
    const mine = ++seq.current;
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await api.get(`${API}/zoho/search-accounts`, { params: { q } });
        if (mine === seq.current) setResults(res.data?.accounts || []);
      } catch (_) {
        if (mine === seq.current) setResults([]);
      } finally {
        if (mine === seq.current) setSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [query, isOpen]);

  if (!isOpen || !item) return null;

  const link = async (account, chosenSector) => {
    setSaving(true);
    try {
      const res = await api.post(`${API}/quotes/automation/${item.id}/link-crm-account`, {
        zoho_account_id: account.id,
        zoho_account_name: account.name || '',
        customer_code: account.customer_code || null,
        market_sector: chosenSector || null,
      });
      if (res.data?.needs_sector) {
        setPending(account);
        setSectorOptions(res.data.sector_options || []);
        return;
      }
      toast.success(`Linked to ${account.name} — future quotes for ${customerName || 'this customer'} will match automatically`);
      onLinked?.({ id: account.id, name: account.name });
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to link the customer');
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center p-4 bg-black/50"
      style={{ zIndex: 10200 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="link-crm-overlay"
    >
      <div className="w-full max-w-[560px] max-h-[90vh] flex flex-col rounded-3xl bg-white shadow-2xl border-2 border-orange-300" data-testid="link-crm-modal">
        <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-orange-100 flex-shrink-0">
          <div className="min-w-0">
            <h3 className="text-base font-extrabold text-gray-900 leading-tight flex items-center gap-2">
              <Link2 className="w-4 h-4" style={{ color: ORANGE }} /> Link to a CRM customer
            </h3>
            <p className="text-[11px] text-gray-500 truncate">Quote for “{customerName || 'Unknown customer'}” isn't linked to a CRM account yet</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 rounded-full p-1.5 hover:bg-gray-100" data-testid="link-crm-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-3 overflow-y-auto">
          {pending ? (
            /* Step 2 — this account isn't in the Phocas Customer Database, so ask for its market sector once */
            <div className="space-y-3" data-testid="link-crm-sector-step">
              <div className="rounded-xl bg-orange-50 border border-orange-200 px-3 py-2 text-xs text-gray-700">
                <span className="font-bold">{pending.name}</span> isn't in the Phocas Customer Database yet, so choose its market sector.
                Future quotes for this customer will then follow that sector's auto-add rule (on/off and £ threshold).
              </div>
              <select
                value={sector}
                onChange={(e) => setSector(e.target.value)}
                className="w-full border-2 border-gray-300 rounded-xl px-3 py-2 text-sm bg-white focus:border-orange-400 focus:outline-none"
                data-testid="link-crm-sector-select"
              >
                <option value="">— Select a market sector —</option>
                {sectorOptions.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setPending(null)} className="rounded-xl h-10 px-4 text-sm">Back</Button>
                <Button
                  onClick={() => link(pending, sector)}
                  disabled={!sector || saving}
                  className="rounded-xl h-10 px-5 text-sm text-white font-semibold"
                  style={{ backgroundColor: ORANGE }}
                  data-testid="link-crm-sector-confirm"
                >
                  {saving ? <><Loader2 className="w-4 h-4 animate-spin mr-1.5" />Linking…</> : 'Link customer'}
                </Button>
              </div>
            </div>
          ) : showNew ? (
            /* Create a brand-new CRM customer, then link it */
            <AddProspectAccountInline
              themeColor="orange"
              onCancel={() => setShowNew(false)}
              onCreated={({ account }) => {
                setShowNew(false);
                link({ id: account.id, name: account.name, customer_code: null });
              }}
            />
          ) : (
            <>
              <div>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-300 pointer-events-none" />
                  <Input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search the CRM for this customer…"
                    className="pl-9 rounded-lg border-2 border-gray-300 h-10"
                    autoFocus
                    data-testid="link-crm-search"
                  />
                </div>
                <div className="border border-gray-200 rounded-xl divide-y max-h-64 overflow-y-auto mt-2 bg-white">
                  {searching ? (
                    <div className="p-3 text-center text-gray-500 text-sm flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Searching…</div>
                  ) : results.length === 0 ? (
                    <div className="p-3 text-center text-gray-400 text-xs">{query.trim().length < 2 ? 'Type at least 2 characters' : 'No CRM accounts found — try a shorter name, or create the customer below'}</div>
                  ) : results.map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      disabled={saving}
                      onClick={() => link(a)}
                      className="w-full text-left p-3 hover:bg-orange-50 transition-colors disabled:opacity-50"
                      data-testid={`link-crm-account-${a.id}`}
                    >
                      <div className="font-semibold text-sm text-gray-900">{a.name}</div>
                      <div className="text-xs text-gray-500">{[a.city, a.customer_code ? `#${a.customer_code}` : null].filter(Boolean).join(' · ')}</div>
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-gray-400 mt-1.5">Picking an account links this customer name to it — future quotes will match it automatically.</p>
              </div>
              <button
                type="button"
                onClick={() => setShowNew(true)}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-orange-600 hover:text-orange-700"
                data-testid="link-crm-new-customer"
              >
                <Building2 className="w-3.5 h-3.5" /> Not in the CRM? Create a new customer
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
